import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCompetitionsDistributionItemsRetrieveQueryOptions } from "@/api/generated/backendAPI";
import type {
  DistributionItemList,
  DistributionItemView,
} from "@/api/generated/backendAPI.schemas";
import {
  MASK,
  groupDistributionItems,
  notStartedMessage,
  shouldShowDistributionCard,
} from "@/utils/distributionItems";

type Props = {
  licenseId: string;
};

/**
 * 運営から配られた秘密の値（API キー・参加者トークンなど）。
 *
 * 値は、この画面のメモリの中だけで扱う。URL・ローカルストレージ・ログには出さない
 * （取得結果は画面を離れたら捨てる）。取得に失敗しても、コンペの画面は壊さずカードだけ隠す。
 */
function DistributionItemsCard({ licenseId }: Props) {
  const { data } = useQuery({
    ...getCompetitionsDistributionItemsRetrieveQueryOptions(licenseId),
    // 画面を離れたら捨てる。開いたとき1回だけ取る（取るたびに閲覧の記録が残る）
    gcTime: 0,
    retry: false,
    throwOnError: false,
    refetchOnWindowFocus: false,
  });
  return <DistributionItemsView data={data} />;
}

type ViewProps = {
  data: DistributionItemList | undefined;
};

export function DistributionItemsView({ data }: ViewProps) {
  if (!shouldShowDistributionCard(data)) return null;
  const groups = groupDistributionItems(data.items);
  return (
    <div className="mt-4 rounded-md border border-[#dbe1e6] bg-white px-5 py-4">
      <p className="text-[15px] text-navcolor font-semibold">配布物</p>
      <p className="mt-1 text-[13px] text-gray-500">
        運営から配られた API キーやトークンです。他の人には共有しないでください。
      </p>
      <div className="mt-3 space-y-4">
        {groups.map((group) => (
          <section key={group.key}>
            <h2 className="text-[13px] font-semibold text-darkcolor">{group.title}</h2>
            <ul className="mt-1 divide-y divide-[#eef1f4]">
              {group.items.map((item) => (
                <DistributionItemRow
                  key={item.id}
                  item={item}
                  started={data.started}
                  startDatetime={data.startDatetime}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

type RowProps = {
  item: DistributionItemView;
  started: boolean;
  startDatetime: string;
};

function DistributionItemRow({ item, started, startDatetime }: RowProps) {
  const [revealed, setRevealed] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  const value = item.value;

  const handleCopy = async () => {
    if (value == null) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopyState("idle"), 2500);
  };

  return (
    <li className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="w-48 shrink-0 text-[14px] font-semibold">{item.label}</span>
      {value != null ? (
        <>
          <span
            className="min-w-0 flex-1 break-all rounded bg-gray-100 px-2 py-1 font-mono text-[13px]"
            aria-label={`${item.label}の値`}
          >
            {revealed ? value : MASK}
          </span>
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            aria-pressed={revealed}
            className="rounded border border-[#dbe1e6] bg-white px-3 py-1 text-[13px] font-normal text-darkcolor hover:bg-gray-50"
          >
            {revealed ? "隠す" : "表示"}
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className="rounded border border-[#dbe1e6] bg-white px-3 py-1 text-[13px] font-normal text-darkcolor hover:bg-gray-50"
          >
            コピー
          </button>
          <span role="status" className="w-40 text-[12px] text-gray-500">
            {copyState === "copied" && "コピーしました"}
            {copyState === "failed" && "コピーできませんでした。表示して手で選んでください"}
          </span>
        </>
      ) : (
        <span className="text-[13px] text-gray-500">
          {started
            ? "値を取得できませんでした。運営にお問い合わせください。"
            : notStartedMessage(startDatetime)}
        </span>
      )}
    </li>
  );
}

export default DistributionItemsCard;
